import * as React from 'react';
import { createContext, useEffect, useMemo, useState } from 'react';

import FeedbackTipsModal from 'bundles/peer/components/modals/FeedbackTipsModal';
import { getReduceLowValueFeedback } from 'bundles/peer/utils/epicUtils';
import type { ReduceLowValueFeedbackType } from 'bundles/peer/utils/epicUtils';
import { dismissFeedbackModal, isFeedbackModalDismissed } from 'bundles/peer/utils/feedbackTipsUtils';

type Props = {
  courseId: string;
  itemId: string;
  children: React.ReactNode;
};

export type ReduceLowValueFeedbackContextType = {
  feedbackTipsEnabled: boolean;
  minCharacterCountEnabled: boolean;
  minCount: number | undefined;
  openFeedbackTips: () => void;
};

export const ReduceLowValueFeedbackContext = createContext<ReduceLowValueFeedbackContextType>({
  feedbackTipsEnabled: false,
  minCharacterCountEnabled: false,
  minCount: undefined,
  openFeedbackTips: () => {
    /* noop */
  },
});

const ReduceLowValueFeedbackProvider = ({ courseId, itemId, children }: Props) => {
  const reduceLowValueFeedbackValue = useMemo<ReduceLowValueFeedbackType>(() => {
    return getReduceLowValueFeedback(courseId);
  }, [courseId]);
  const [showFeedbackTips, setShowFeedbackTips] = useState(false);

  const handleFeedbackTipsOpen = () => {
    setShowFeedbackTips(true);
  };

  const handleFeedbackTipsClose = () => {
    setShowFeedbackTips(false);
    dismissFeedbackModal(courseId, itemId);
  };

  useEffect(() => {
    if (!isFeedbackModalDismissed(courseId, itemId)) {
      if (reduceLowValueFeedbackValue.isB || reduceLowValueFeedbackValue.isC) {
        handleFeedbackTipsOpen();
      }
    }
  }, [courseId, itemId, reduceLowValueFeedbackValue]);

  return (
    <ReduceLowValueFeedbackContext.Provider
      value={{
        feedbackTipsEnabled: reduceLowValueFeedbackValue.isB || reduceLowValueFeedbackValue.isC,
        minCharacterCountEnabled: reduceLowValueFeedbackValue.isA || reduceLowValueFeedbackValue.isC,
        minCount: reduceLowValueFeedbackValue.minCount,
        openFeedbackTips: handleFeedbackTipsOpen,
      }}
    >
      {showFeedbackTips && <FeedbackTipsModal onClose={handleFeedbackTipsClose} />}
      {children}
    </ReduceLowValueFeedbackContext.Provider>
  );
};

export default ReduceLowValueFeedbackProvider;
